import { Chip, OutlinedInput, Portal } from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import { RightSidebar } from "../../../../job-page-components/right-sidebar.component";
import { useRefContext } from "../../../../../../context/RefContext";
import "./styles.css";
import { ItemListing } from "./item-listing.component";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import {
	TariffWithPricesViewType,
	useCatalogsStore,
} from "@/dentlab/src/store/Catalogs";
import { showNotification } from "@/dentlab/src/store/Central/selectors";

export const JobItemsTariffsSelection: React.FC<{
	rows: TariffWithPricesViewType[];
	onAddTariffById: (
		code_e: string,
		id: string,
		options?: { force: boolean }
	) => void;
	jobItemCodes: { code_e: string }[];
	jobDocumentId: number;
	isNewStyle: boolean;
	defaultCatalogId: string | null;
}> = ({
	rows,
	onAddTariffById,
	jobItemCodes,
	jobDocumentId,
	isNewStyle,
	defaultCatalogId,
}) => {
	const label = "Leistungen";
	const { openedSidebar, closeSidebar } = useJobStore((state) => ({
		openedSidebar: state.openedSidebar,
		closeSidebar: state.closeSidebar,
	}));

	const { catalogs, categories, loadCategories } = useCatalogsStore(
		(state) => ({
			catalogs: state.catalogs,
			categories: state.categories,
			loadCategories: state.loadCategories,
		})
	);

	const open =
		openedSidebar?.type === "tariff" &&
		openedSidebar?.documentId === jobDocumentId;

	// Deactivated catalogs must not be selectable when adding tariffs to a job.
	const activeCatalogs = useMemo(
		() => catalogs.filter((catalog) => !catalog.is_deactivated),
		[catalogs]
	);

	const initialCatalogId = useMemo(() => {
		const defaultIsActive = activeCatalogs.some(
			(catalog) => catalog.id === defaultCatalogId
		);
		if (defaultIsActive) {
			return defaultCatalogId;
		}
		return activeCatalogs.length > 0 ? activeCatalogs[0].id : null;
	}, [defaultCatalogId, activeCatalogs]);

	const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(
		initialCatalogId
	);
	const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
		null
	);
	const [searchTerm, setSearchTerm] = useState("");

	const handleClose = () => {
		closeSidebar();
	};

	const handleSelect = (
		tariffId: string | null,
		code_e: string,
		options?: { force: boolean }
	) => {
		if (!tariffId) {
			showNotification({
				message: "Tarifeingabecode fehlt",
				type: "error",
			});
			return;
		}
		onAddTariffById(code_e, tariffId, options);
	};

	// Load categories when catalog changes
	useEffect(() => {
		if (selectedCatalogId) {
			loadCategories(selectedCatalogId);
		}
	}, [selectedCatalogId, loadCategories]);

	// Set initial catalog selection once
	useEffect(() => {
		if (!selectedCatalogId && initialCatalogId) {
			setSelectedCatalogId(initialCatalogId);
		}
	}, [selectedCatalogId, initialCatalogId]);

	const handleCatalogChange = (catalogId: string) => {
		setSelectedCatalogId(catalogId);
		setSelectedCategoryId(null);
		setSearchTerm("");
	};

	const handleCategoryChange = (categoryId: string) => {
		setSelectedCategoryId(categoryId);
		setSearchTerm("");
	};

	const { container } = useRefContext();

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const handleClick = (
		e: React.MouseEvent<HTMLDivElement>,
		row: TariffWithPricesViewType
	) => {
		const tariff = row as TariffWithPricesViewType;
		if (!tariff.id) {
			showNotification({
				message: "Tarifeingabecode fehlt",
				type: "error",
			});
			return;
		}
		if (!tariff.code_e) {
			showNotification({
				message: "Tarifeingabecode fehlt",
				type: "error",
			});
			return;
		}
		if (e.ctrlKey || e.metaKey) {
			handleSelect(tariff.id, tariff.code_e, { force: true });
			return;
		}
		handleSelect(tariff.id, tariff.code_e);
	};

	// Get categories for selected catalog
	const catalogCategories = useMemo(() => {
		if (!selectedCatalogId) return [];
		return categories.filter((cat) => {
			if (!cat.catalog_id) return false;
			return cat.catalog_id === selectedCatalogId;
		});
	}, [categories, selectedCatalogId]);

	const visibleRows = useMemo(
		() =>
			rows.filter((row) => {
				if (!row.code_e) {
					return false;
				}

				// Search filter
				if (searchTerm) {
					const regex = new RegExp(searchTerm, "i");
					return (
						row.description_de?.match(regex) ||
						row.code_e?.match(regex) ||
						row.code?.match(regex)
					);
				}

				// Category filter
				if (selectedCategoryId) {
					return row.category_id === selectedCategoryId;
				}

				// Catalog filter (when no category selected)
				if (selectedCatalogId) {
					return row.catalog_id === selectedCatalogId;
				}

				return true;
			}),
		[rows, searchTerm, selectedCategoryId, selectedCatalogId]
	);

	return (
		<>
			{open && (
				<Portal container={container.current}>
					<RightSidebar
						title={label}
						onClose={handleClose}
						isNewStyle={isNewStyle}
					>
						<div
							style={{
								padding: "10px",
								borderBottom: "var(--border-sm)",
							}}
						>
							{/* Search bar */}
							<div style={{ marginBottom: "10px" }}>
								<OutlinedInput
									type="text"
									style={{
										fontSize: "14px",
										width: "100%",
									}}
									value={searchTerm}
									onChange={handleSearch}
									placeholder="Suche nach Code oder Bezeichnung..."
								/>
							</div>

							{/* Catalog selection */}
							<div
								style={{
									display: "flex",
									flexDirection: "row",
									flexWrap: "wrap",
									gap: "5px",
									marginBottom: "10px",
								}}
							>
								{activeCatalogs.map((catalog) => (
									<Chip
										key={catalog.id}
										label={catalog.name}
										onClick={() =>
											handleCatalogChange(catalog.id!)
										}
										variant={
											selectedCatalogId === catalog.id
												? "filled"
												: "outlined"
										}
										color="primary"
										size="small"
									/>
								))}
							</div>

							{/* Category selection */}
							{selectedCatalogId &&
								catalogCategories.length > 0 && (
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											flexWrap: "wrap",
											gap: "5px",
										}}
									>
										<Chip
											label="Alle"
											onClick={() =>
												setSelectedCategoryId(null)
											}
											variant={
												!selectedCategoryId
													? "filled"
													: "outlined"
											}
											size="small"
										/>
										{catalogCategories.map((category) => (
											<Chip
												key={category.id}
												label={category.name}
												onClick={() =>
													handleCategoryChange(
														category.id!
													)
												}
												variant={
													selectedCategoryId ===
													category.id
														? "filled"
														: "outlined"
												}
												size="small"
												style={{
													opacity:
														category.is_deactivated
															? 0.5
															: 1,
												}}
											/>
										))}
									</div>
								)}
						</div>
						<div
							style={{
								position: "relative",
							}}
						>
							<ItemListing
								jobItemCodes={jobItemCodes}
								rows={visibleRows}
								handleClick={handleClick}
							/>
						</div>
					</RightSidebar>
				</Portal>
			)}
		</>
	);
};
